import type { LedgerHints } from '@snappy/core/lib/hl/ledger';

// What Hyperliquid fills cannot say about the orders this browser placed (web port of InOut.Mobile
// `features/trading/hooks/ledgerHints.ts`). The V2 backend records them when it places orders; the trade history
// (`buildTrades`) and the V2 trade notifications read them.
//
// Differences from the phone:
// - localStorage (synchronous) instead of AsyncStorage, so reads never wait for a load;
// - one map per wallet address, so two wallets used on one browser never mix their labels;
// - the web places GTC limit orders, which the phone does not: `limitEntryOidByCoin` names the coin's resting entry
//   and `limitOrders` keeps what each limit order knew about itself, plus the wanted TP/SL prices of the current
//   position (`tpPxByCoin` / `slPxByCoin`) that the trade rows report and `ensureTriggers` restores.
//
// Engine-free on purpose (a type import only): the notification hook reads it from the V1 bundle.

export const LEDGER_HINTS_KEY = 'snappy.v2.ledgerHints.v1';

/** A TP or SL leg as the order form asked for it: an absolute price, or a price offset from the entry in bps. */
export type TriggerRequest = { price: string } | { bps: number };

/** What a GTC limit order knew about itself when it was placed, by its entry oid. Kept after the fill or a cancel. */
export interface LimitOrderHint {
    coin: string;
    isLong: boolean;
    leverage: number;
    /** The rounded price the order rests at, canonical decimal. */
    limitPrice: string;
    /** Order size in asset units, canonical decimal. */
    size: string;
    /** The collateral the order was sized from, USDC base units (6 decimals). */
    marginUnits: string;
    placedAt: number;
    takeProfit: TriggerRequest | null;
    stopLoss: TriggerRequest | null;
    /** The legs resolved against the limit price, canonical decimals. */
    tpPx: string | null;
    slPx: string | null;
    /** The `normalTpsl` children's oids: they activate only once the entry fills in full. */
    childTpOid: number | null;
    childSlOid: number | null;
    /** Set once a cancel confirmed the order never filled: history shows it as Cancelled. */
    cancelledAt: number | null;
}

export interface WebLedgerHints extends LedgerHints {
    /** The coin's tracked limit entry while it can still fill (resting, possibly partly filled). */
    limitEntryOidByCoin: Record<string, number>;
    limitOrders: Record<string, LimitOrderHint>;
    /** The TP/SL prices wanted for the coin's current position (canonical decimals). */
    tpPxByCoin: Record<string, string>;
    slPxByCoin: Record<string, string>;
    /** The live size the TP/SL cover of a partly filled limit entry was last placed for. */
    limitCoverSzByCoin: Record<string, string>;
}

type HintField = keyof WebLedgerHints;

/** Entries to add or replace per field; a `null` entry removes it. Fields and entries left out are kept. */
export type WebLedgerHintsPatch = {
    [K in HintField]?: Record<string, WebLedgerHints[K][string] | null>;
};

/** The ledger hints of one wallet: what the V2 backend reads and writes. */
export interface LedgerHintsStore {
    read(): WebLedgerHints;
    update(patch: WebLedgerHintsPatch): WebLedgerHints;
}

/** Oid-keyed maps keep this many entries (the highest oids, i.e. the newest orders). */
const MAX_OID_ENTRIES = 500;
const OID_KEYED: readonly HintField[] = [
    'triggerOids',
    'leverageByEntryOid',
    'limitOrders',
];

const isNumber = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);
const isString = (value: unknown): value is string => typeof value === 'string';
const isNullableNumber = (value: unknown): value is number | null =>
    value === null || isNumber(value);
const isNullableString = (value: unknown): value is string | null =>
    value === null || isString(value);

function isTriggerRequest(value: unknown): value is TriggerRequest | null {
    if (value === null) {
        return true;
    }
    if (typeof value !== 'object') {
        return false;
    }
    const leg = value as Record<string, unknown>;
    return isString(leg.price) || isNumber(leg.bps);
}

function isLimitOrderHint(value: unknown): value is LimitOrderHint {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const h = value as Record<string, unknown>;
    return (
        isString(h.coin) &&
        typeof h.isLong === 'boolean' &&
        isNumber(h.leverage) &&
        isString(h.limitPrice) &&
        isString(h.size) &&
        isString(h.marginUnits) &&
        isNumber(h.placedAt) &&
        isTriggerRequest(h.takeProfit) &&
        isTriggerRequest(h.stopLoss) &&
        isNullableString(h.tpPx) &&
        isNullableString(h.slPx) &&
        isNullableNumber(h.childTpOid) &&
        isNullableNumber(h.childSlOid) &&
        isNullableNumber(h.cancelledAt)
    );
}

const FIELD_VALUE_CHECKS: {
    [K in HintField]: (value: unknown) => value is WebLedgerHints[K][string];
} = {
    tpOids: isNumber,
    slOids: isNumber,
    leverageByEntryOid: isNumber,
    entryOidByCoin: isString,
    triggerOids: (value): value is 'tp' | 'sl' =>
        value === 'tp' || value === 'sl',
    limitEntryOidByCoin: isNumber,
    limitOrders: isLimitOrderHint,
    tpPxByCoin: isString,
    slPxByCoin: isString,
    limitCoverSzByCoin: isString,
};

const FIELDS = Object.keys(FIELD_VALUE_CHECKS) as HintField[];

export function emptyLedgerHints(): WebLedgerHints {
    return {
        tpOids: {},
        slOids: {},
        triggerOids: {},
        leverageByEntryOid: {},
        entryOidByCoin: {},
        limitEntryOidByCoin: {},
        limitOrders: {},
        tpPxByCoin: {},
        slPxByCoin: {},
        limitCoverSzByCoin: {},
    };
}

/** One wallet's stored hints with anything malformed dropped: a missing or wrong-typed field reads as empty. */
function sanitize(stored: unknown): WebLedgerHints {
    const hints = emptyLedgerHints();
    if (!stored || typeof stored !== 'object') {
        return hints;
    }
    for (const field of FIELDS) {
        const record: unknown = (stored as Record<string, unknown>)[field];
        if (!record || typeof record !== 'object') {
            continue;
        }
        const check = FIELD_VALUE_CHECKS[field];
        const kept: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(record)) {
            if (check(value)) {
                kept[key] = value;
            }
        }
        (hints as unknown as Record<HintField, Record<string, unknown>>)[
            field
        ] = kept;
    }
    return hints;
}

/** Keeps the newest {@link MAX_OID_ENTRIES} entries of an oid-keyed map (oids only grow). */
function capByOid(record: Record<string, unknown>): Record<string, unknown> {
    const keys = Object.keys(record);
    if (keys.length <= MAX_OID_ENTRIES) {
        return record;
    }
    const kept = keys
        .sort((a, b) => Number(b) - Number(a))
        .slice(0, MAX_OID_ENTRIES);
    return Object.fromEntries(kept.map((key) => [key, record[key]]));
}

function applyPatch(
    hints: WebLedgerHints,
    patch: WebLedgerHintsPatch,
): WebLedgerHints {
    const next = { ...hints };
    for (const field of FIELDS) {
        const changes = patch[field];
        if (!changes) {
            continue;
        }
        let record: Record<string, unknown> = { ...hints[field] };
        for (const [key, value] of Object.entries(changes)) {
            if (value === null) {
                delete record[key];
            } else {
                record[key] = value;
            }
        }
        if (OID_KEYED.includes(field)) {
            record = capByOid(record);
        }
        (next as unknown as Record<HintField, Record<string, unknown>>)[field] =
            record;
    }
    return next;
}

interface StoredHints {
    v: 1;
    wallets: Record<string, unknown>;
}

function storage(): Storage | null {
    try {
        return globalThis.localStorage ?? null;
    } catch {
        return null;
    }
}

function readStored(): StoredHints {
    try {
        const raw = storage()?.getItem(LEDGER_HINTS_KEY) ?? null;
        const parsed: unknown = raw === null ? null : JSON.parse(raw);
        if (
            parsed &&
            typeof parsed === 'object' &&
            (parsed as { v?: unknown }).v === 1 &&
            typeof (parsed as { wallets?: unknown }).wallets === 'object' &&
            (parsed as { wallets?: unknown }).wallets !== null
        ) {
            return parsed as StoredHints;
        }
    } catch {
        // unreadable: start over
    }
    return { v: 1, wallets: {} };
}

function writeStored(stored: StoredHints): void {
    try {
        storage()?.setItem(LEDGER_HINTS_KEY, JSON.stringify(stored));
    } catch {
        // kept in memory for this session
    }
}

// Parsed hints per wallet (lowercased address), dropped whenever another tab writes the key.
const cache = new Map<string, WebLedgerHints>();
const listeners = new Set<() => void>();
let storageListening = false;

function notify(): void {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch (err) {
            // one failing reader must not keep the hints from the others
            console.warn('[ledgerHints] a listener failed', err);
        }
    });
}

function listenToOtherTabs(): void {
    if (storageListening || typeof window === 'undefined') {
        return;
    }
    storageListening = true;
    window.addEventListener('storage', (event) => {
        if (event.key === LEDGER_HINTS_KEY || event.key === null) {
            cache.clear();
            notify();
        }
    });
}

function walletKey(address: string): string {
    return address.toLowerCase();
}

/** The wallet's hints. Stable until they change. */
export function readLedgerHints(address: string): WebLedgerHints {
    listenToOtherTabs();
    const key = walletKey(address);
    const cached = cache.get(key);
    if (cached) {
        return cached;
    }
    const hints = sanitize(readStored().wallets[key]);
    cache.set(key, hints);
    return hints;
}

/** Merges `patch` into the wallet's hints and stores them; returns the updated hints. A failed write is not an error. */
export function updateLedgerHints(
    address: string,
    patch: WebLedgerHintsPatch,
): WebLedgerHints {
    const key = walletKey(address);
    const next = applyPatch(readLedgerHints(address), patch);
    cache.set(key, next);
    const stored = readStored();
    writeStored({ v: 1, wallets: { ...stored.wallets, [key]: next } });
    notify();
    return next;
}

/** Forgets one wallet's hints (its key was removed from this browser). */
export function clearLedgerHints(address: string): void {
    const key = walletKey(address);
    cache.delete(key);
    const stored = readStored();
    if (key in stored.wallets) {
        const wallets = { ...stored.wallets };
        delete wallets[key];
        writeStored({ v: 1, wallets });
    }
    notify();
}

export function subscribeLedgerHints(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** The read/update pair bound to one wallet, as the V2 backend takes it. */
export function ledgerHintsFor(address: string): LedgerHintsStore {
    return {
        read: () => readLedgerHints(address),
        update: (patch) => updateLedgerHints(address, patch),
    };
}

/** Tests only: drops the in-memory copies so the next read comes from localStorage. */
export function resetLedgerHintsCache(): void {
    cache.clear();
}

/**
 * Forgets the per-position hints of a trade reported closed, but only while `entryOid` is still the coin's recorded
 * entry: a close reported late must never wipe the hints of a newer trade on the same coin. The per-oid labels and
 * the limit orders stay, so the closed trade keeps its TP/SL status and its limit price.
 */
export function forgetClosedTradeHints(
    address: string,
    coin: string,
    entryOid: string,
): void {
    if (readLedgerHints(address).entryOidByCoin[coin] !== entryOid) {
        return;
    }
    updateLedgerHints(address, {
        entryOidByCoin: { [coin]: null },
        tpOids: { [coin]: null },
        slOids: { [coin]: null },
        tpPxByCoin: { [coin]: null },
        slPxByCoin: { [coin]: null },
        limitCoverSzByCoin: { [coin]: null },
    });
}
